import { useCallback, useEffect, useState } from "react";
import { Loader2, Scale, Wand2, Save, Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from "../../../components/ui";
import { Textarea } from "../../../components/ui/textarea";
import { formatMelbourneDateTime } from "../../../utils/melbourneTime";
import { getPricingEvidence, prefillPricingEvidence, savePricingEvidence } from "../api/complianceApi";

const EMPTY_COMP = { address: "", sold_price: "", sold_date: "", distance_km: "", reason_selected: "", agent_notes: "", source: "manual" };
const MAX = 3;

const fromEvidence = (ev) => ev ? ({
  estimate_low: ev.estimate_low ?? "", estimate_high: ev.estimate_high ?? "", suburb_median: ev.suburb_median ?? "",
  analysis_date: ev.analysis_date || "", data_sources: ev.data_sources || "", agent_notes: ev.agent_notes || "",
  fewer_than_three_reason: ev.fewer_than_three_reason || "", is_metropolitan: ev.is_metropolitan !== false,
  cma_report: ev.cma_report || null,
  comparables: (ev.comparables || []).map((c) => ({ ...EMPTY_COMP, ...c, sold_price: c.sold_price ?? "", distance_km: c.distance_km ?? "" })),
}) : null;

const blankForm = () => ({
  estimate_low: "", estimate_high: "", suburb_median: "", analysis_date: new Date().toISOString().slice(0, 10),
  data_sources: "", agent_notes: "", fewer_than_three_reason: "", is_metropolitan: true, cma_report: null,
  comparables: [{ ...EMPTY_COMP }],
});

/**
 * Comparable-sales / pricing substantiation (CAV can ask why the three
 * comparables were chosen). Every save is a new version; the 2 km / 6 month
 * (metro) checks are warnings only.
 */
export default function PricingEvidenceCard({ propertyId, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState(null);
  const [refDate, setRefDate] = useState("");
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState(blankForm());
  const [warnings, setWarnings] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await getPricingEvidence(propertyId);
      setCurrent(res.data.evidence);
      setRefDate(res.data.reference_date);
      setHistory(res.data.history || []);
    } catch {
      /* the tab shows its own error */
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    setForm(fromEvidence(current) || blankForm());
    setWarnings([]);
    setEditing(true);
  };

  const prefill = async () => {
    try {
      const res = await prefillPricingEvidence(propertyId);
      if (!res.data?.source) { toast("No published Property Price Statement is linked to this property yet."); return; }
      setForm((f) => ({
        ...f, ...res.data,
        comparables: (res.data.comparables || []).length ? res.data.comparables.map((c) => ({ ...EMPTY_COMP, ...c })) : f.comparables,
      }));
      toast.success("Filled from the published Property Price Statement");
    } catch {
      toast.error("Could not load the price statement");
    }
  };

  const setComp = (i, key, value) => setForm((f) => ({
    ...f, comparables: f.comparables.map((c, idx) => (idx === i ? { ...c, [key]: value } : c)),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await savePricingEvidence(propertyId, form);
      setWarnings(res.data.warnings || []);
      toast.success(res.data.warnings?.length ? "Saved with warnings" : "Pricing evidence saved");
      setEditing(false);
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const view = current;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-black uppercase tracking-[0.15em] text-gray-500 flex items-center gap-2">
          <Scale className="h-4 w-4" /> Pricing / comparable-sales evidence
          {!editing && (
            <Button size="sm" variant="outline" className="ml-auto normal-case tracking-normal" onClick={startEdit}>
              {current ? "New version" : "Record evidence"}
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 text-sm">
        {loading ? <div className="flex items-center gap-2 text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div> : null}

        {!loading && !editing && !view && (
          <p className="text-gray-500">No comparables recorded yet. Record the three comparable sales and why each was chosen.</p>
        )}

        {!loading && !editing && view && (
          <div className="space-y-2">
            <div className="text-xs text-gray-500">
              Version saved {formatMelbourneDateTime(view.created_at)} · {history.length} version{history.length === 1 ? "" : "s"} kept ·
              {view.is_metropolitan ? " Melbourne metro rules (2 km / 6 months)" : " regional rules (5 km / 18 months)"}
              {refDate ? ` measured from ${refDate}` : ""}
            </div>
            <ol className="space-y-2">
              {view.comparables.map((c) => (
                <li key={c.id} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                  <div className="font-medium">{c.position}. {c.address}</div>
                  <div className="text-xs text-gray-600">
                    {c.sold_price ? `$${Number(c.sold_price).toLocaleString()}` : "no price"} · {c.sold_date || "no date"} · {c.distance_km != null ? `${c.distance_km} km` : "distance unknown"}
                  </div>
                  {c.reason_selected && <div className="text-xs text-gray-700 mt-1">Why: {c.reason_selected}</div>}
                  {(c.flags || []).map((f, i) => <div key={i} className="text-xs text-amber-700">⚠ {f}</div>)}
                </li>
              ))}
            </ol>
            {view.fewer_than_three_reason && <div className="text-xs text-gray-600">Fewer than three: {view.fewer_than_three_reason}</div>}
          </div>
        )}

        {editing && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <Button size="sm" variant="outline" onClick={prefill}><Wand2 className="h-4 w-4 mr-1" /> Fill from published PPS</Button>
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input type="checkbox" checked={form.is_metropolitan}
                  onChange={(e) => setForm((f) => ({ ...f, is_metropolitan: e.target.checked }))} />
                Melbourne metropolitan property (2 km / 6 months)
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div><Label>Estimate from</Label><Input value={form.estimate_low} onChange={(e) => setForm((f) => ({ ...f, estimate_low: e.target.value }))} /></div>
              <div><Label>Estimate to</Label><Input value={form.estimate_high} onChange={(e) => setForm((f) => ({ ...f, estimate_high: e.target.value }))} /></div>
              <div><Label>Suburb median</Label><Input value={form.suburb_median} onChange={(e) => setForm((f) => ({ ...f, suburb_median: e.target.value }))} /></div>
            </div>
            {form.comparables.map((c, i) => (
              <div key={i} className="rounded-lg border border-gray-200 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Comparable {i + 1}</span>
                  <Button size="sm" variant="ghost" className="text-red-600" aria-label={`Remove comparable ${i + 1}`}
                    onClick={() => setForm((f) => ({ ...f, comparables: f.comparables.filter((_, idx) => idx !== i) }))}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <Input placeholder="Address" aria-label={`Comparable ${i + 1} address`} value={c.address} onChange={(e) => setComp(i, "address", e.target.value)} />
                <div className="grid gap-2 sm:grid-cols-3">
                  <Input placeholder="Sold price" value={c.sold_price} onChange={(e) => setComp(i, "sold_price", e.target.value)} />
                  <Input type="date" value={c.sold_date} onChange={(e) => setComp(i, "sold_date", e.target.value)} />
                  <Input placeholder="Distance (km)" value={c.distance_km} onChange={(e) => setComp(i, "distance_km", e.target.value)} />
                </div>
                <Textarea rows={2} placeholder="Why this comparable was selected" aria-label={`Comparable ${i + 1} reason`}
                  value={c.reason_selected} onChange={(e) => setComp(i, "reason_selected", e.target.value)} />
              </div>
            ))}
            {form.comparables.length < MAX && (
              <Button size="sm" variant="outline" onClick={() => setForm((f) => ({ ...f, comparables: [...f.comparables, { ...EMPTY_COMP }] }))}>
                <Plus className="h-4 w-4 mr-1" /> Add comparable
              </Button>
            )}
            {form.comparables.filter((c) => c.address.trim()).length < MAX && (
              <div>
                <Label>Why fewer than three comparables</Label>
                <Textarea rows={2} value={form.fewer_than_three_reason}
                  onChange={(e) => setForm((f) => ({ ...f, fewer_than_three_reason: e.target.value }))}
                  placeholder="e.g. Fewer than three comparable sales within 2 km in the last 6 months" />
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>Data sources</Label><Input value={form.data_sources} onChange={(e) => setForm((f) => ({ ...f, data_sources: e.target.value }))} /></div>
              <div><Label>Analysis date</Label><Input type="date" value={form.analysis_date} onChange={(e) => setForm((f) => ({ ...f, analysis_date: e.target.value }))} /></div>
            </div>
            <Textarea rows={2} placeholder="Agent notes supporting comparability" value={form.agent_notes}
              onChange={(e) => setForm((f) => ({ ...f, agent_notes: e.target.value }))} />
            <div className="flex gap-2">
              <Button onClick={save} disabled={saving}><Save className="h-4 w-4 mr-1" />{saving ? "Saving…" : "Save version"}</Button>
              <Button variant="outline" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
            </div>
          </div>
        )}

        {warnings.length > 0 && !editing && (
          <ul className="mt-3 space-y-1 text-xs text-amber-700">{warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}</ul>
        )}
      </CardContent>
    </Card>
  );
}
